import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { bool, object, string } from 'prop-types';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requestProgressiveAuth } from '../../util/authIntent';
import { createTownHallPost, fetchTownHallPosts } from '../../util/api';
import { userDisplayNameAsString } from '../../util/data';

import { PrimaryButton, SecondaryButton } from '../Button/Button';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './TownHallFeed.module.css';

const MAX_CONTENT_LENGTH = 500;

const formatPostTime = (isoString, intl) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return intl.formatDate(date, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

const TownHallFeed = ({
  isAuthenticated = false,
  currentUser = null,
  className = null,
  rootClassName = null,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetchTownHallPosts()
      .then(data => {
        setPosts(data.posts || []);
      })
      .catch(e => {
        setError(e.message || 'Failed to load feed');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleBroadcast = e => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed || submitting) {
      return;
    }

    const publish = () => {
      setSubmitting(true);
      setError(null);
      createTownHallPost(trimmed)
        .then(data => {
          setPosts(prev => [data.post, ...prev.filter(p => p.id !== data.post.id)]);
          setContent('');
        })
        .catch(err => {
          setError(err.message || 'Failed to publish');
        })
        .finally(() => {
          setSubmitting(false);
        });
    };

    if (!isAuthenticated) {
      requestProgressiveAuth({
        history,
        routeConfiguration,
        location,
        intent: 'townHall',
        onAuthenticated: publish,
      });
      return;
    }

    publish();
  };

  const handleGuestPostIntent = () => {
    requestProgressiveAuth({
      history,
      routeConfiguration,
      location,
      intent: 'townHall',
    });
  };

  const displayName = userDisplayNameAsString(
    currentUser,
    intl.formatMessage({ id: 'TownHallFeed.anonymousAuthor' })
  );
  const remaining = MAX_CONTENT_LENGTH - content.length;

  return (
    <section className={classNames(rootClassName || css.root, className)}>
      <header className={css.header}>
        <div>
          <h2 className={css.title}>
            <FormattedMessage id="TownHallFeed.title" />
          </h2>
          <p className={css.subtitle}>
            <FormattedMessage id="TownHallFeed.subtitle" />
          </p>
        </div>
        <span className={css.liveBadge}>
          <FormattedMessage id="TownHallFeed.liveBadge" />
        </span>
      </header>

      {isAuthenticated ? (
        <form className={css.composer} onSubmit={handleBroadcast}>
          <div className={css.composerMeta}>
            <span className={css.avatar}>{displayName.charAt(0).toUpperCase()}</span>
            <span className={css.composerLabel}>{displayName}</span>
          </div>
          <textarea
            className={css.textarea}
            value={content}
            onChange={ev => setContent(ev.target.value.slice(0, MAX_CONTENT_LENGTH))}
            placeholder={intl.formatMessage({ id: 'TownHallFeed.placeholder' })}
            rows={3}
            maxLength={MAX_CONTENT_LENGTH}
          />
          <div className={css.composerActions}>
            <span className={css.charCount}>{remaining}</span>
            <PrimaryButton type="submit" inProgress={submitting} disabled={!content.trim()}>
              <FormattedMessage id="TownHallFeed.broadcastButton" />
            </PrimaryButton>
          </div>
        </form>
      ) : (
        <div className={css.guestComposer}>
          <SecondaryButton className={css.guestPostButton} onClick={handleGuestPostIntent}>
            <FormattedMessage id="TownHallFeed.postIntentButton" />
          </SecondaryButton>
        </div>
      )}

      {error ? <p className={css.error}>{error}</p> : null}

      <div className={css.feed}>
        {loading ? (
          <div className={css.loading}>
            <IconSpinner />
          </div>
        ) : posts.length === 0 ? (
          <p className={css.empty}>
            <FormattedMessage id="TownHallFeed.emptyFeed" />
          </p>
        ) : (
          <ul className={css.postList}>
            {posts.map(post => (
              <li key={post.id} className={css.post}>
                <div className={css.postHeader}>
                  <span className={css.postAvatar}>
                    {(post.authorDisplayName || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className={css.postMeta}>
                    <span className={css.postAuthor}>{post.authorDisplayName}</span>
                    <time className={css.postTime} dateTime={post.createdAt}>
                      {formatPostTime(post.createdAt, intl)}
                    </time>
                  </div>
                </div>
                <p className={css.postContent}>{post.content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

TownHallFeed.propTypes = {
  isAuthenticated: bool,
  currentUser: object,
  className: string,
  rootClassName: string,
};

export default TownHallFeed;
